import { hexToHsva, hsvaToHexa } from '@meitu/saas-colors/dist/esm/utils/color-convert';
import { type ITexture } from '@meitu/saas-editor-core/es/version-types';
import { baseTrack, FontBaseTracker, type BaseTrackPayload } from '@/utils/tracker';

type Payload = Omit<BaseTrackPayload, 'funcId'>;

export const Tracker = {
    ...FontBaseTracker,

    /**
     * 设置图层透明度
     */
    setLayerOpacity(payload: Payload & { opacity: number }) {
        const { opacity, ...rest } = payload;
        const basePayload = { ...rest, funcId: '1_21' };
        baseTrack(basePayload, ['click', 'use']);

        const params = {
            ['不透明度']: opacity,
        };

        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 打开颜色选择器
     */
    openColorPicker(payload: Payload & { kind: string }) {
        const { kind, ...rest } = payload;

        let secondID = '2';
        switch (kind) {
            case 'color':
                secondID = '2';
                break;
            case 'textStroke':
                secondID = '19';
                break;
            case 'textShadow':
                secondID = '20';
                break;
            case 'textGlow':
                secondID = '21';
                break;
            case 'textMark':
                secondID = '161';
                break;
            default:
        }

        const basePayload = { ...rest, funcId: `1_${secondID}` };
        baseTrack(basePayload, ['click']);
    },

    /**
     * 设置文字填充色
     */
    setFillColor(payload: Payload & { color: string }) {
        const { color, ...rest } = payload;

        const basePayload = { ...rest, funcId: '1_18' };
        baseTrack(basePayload, ['use']);

        const params = {
            ['字体颜色']: color,
        };

        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 设置文字填充透明度
     */
    setFillOpacity(payload: Payload & { opacity: number; color: string }) {
        const { opacity, color, ...rest } = payload;

        const basePayload = { ...rest, funcId: '1_18' };
        baseTrack(basePayload, ['click', 'use']);

        const hsva = hexToHsva(color);
        hsva.a = opacity;
        const finalColor = hsvaToHexa(hsva);

        const params = {
            ['字体颜色']: finalColor,
        };

        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 设置填充纹理
     */
    setTexture(payload: Payload & { texture: ITexture }) {
        const { texture, ...rest } = payload;

        const basePayload = { ...rest, funcId: '1_36' };

        const timings: Array<'click' | 'use'> = [];
        if (texture.url) timings.push('use');

        baseTrack(basePayload, timings);

        if (texture.url) {
            window._stat_track('hb_text_edit_yes_data', {
                patterned: texture.name ? 'recommend' : 'upload',
                fill_type: texture.stretch,
            });
        }
    },

    /**
     * 使用文本格式刷
     */
    useTextBrush(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_73' };
        baseTrack(basePayload, ['click']);
    },

    /**
     * 打开 AI 花字
     */
    openArtTextPlugin(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_78' };
        baseTrack(basePayload, ['click']);
    },

    /**
     * 使用文字特效预设
     */
    useTextEffectPreset(payload: Payload & { index: number }) {
        const { index, ...rest } = payload;

        const basePayload = { ...rest, funcId: '1_17' };
        baseTrack(basePayload, ['click', 'use']);

        window._stat_track('hb_text_edit_yes_data', {
            样式: index,
        });
    },

    /**
     * 使用文字特效预设
     */
    useArtText(payload: Payload & { id: string }) {
        const { id, ...rest } = payload;

        const basePayload = { ...rest, funcId: '1_17' };
        baseTrack(basePayload, ['click', 'use']);

        window._stat_track('hb_text_edit_yes_data', {
            id,
        });
    },

    /**
     * 添加描边
     */
    addStroke(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_19' };
        baseTrack(basePayload, ['click', 'use']);

        const params = {
            ['文字特效']: '描边',
        };

        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 移除描边
     */
    removeStroke(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_19' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 切换可见性
     */
    toggleStroke(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_19' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 调整描边属性
     */
    adjustStrokeProps(payload: Payload & { prop: string; value: number | string }) {
        const { prop, value, ...rest } = payload;

        const basePayload = { ...rest, funcId: '1_19' };
        const timings: Array<'click' | 'use'> = prop !== 'color' ? ['click', 'use'] : ['use'];

        baseTrack(basePayload, timings);
    },

    /**
     * 添加阴影
     */
    addShadow(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_20' };
        baseTrack(basePayload, ['click', 'use']);

        const params = {
            ['文字特效']: '投影',
        };

        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 移除阴影
     */
    removeShadow(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_20' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 切换可见性
     */
    toggleShadow(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_20' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 调整阴影属性
     */
    adjustShadowProps(payload: Payload & { prop: string; value: number | string }) {
        const { prop, value, ...rest } = payload;

        const basePayload = { ...rest, funcId: '1_20' };
        const timings: Array<'click' | 'use'> = prop !== 'color' ? ['click', 'use'] : ['use'];

        baseTrack(basePayload, timings);
    },

    /**
     * 添加外发光
     */
    addGlow(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_49' };
        baseTrack(basePayload, ['click', 'use']);

        const params = {
            ['文字特效']: '荧光',
        };

        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 移除外发光
     */
    removeGlow(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_49' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 切换可见性
     */
    toggleGlow(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_49' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 调整外发光属性
     */
    adjustGlowProps(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_49' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 添加背景
     */
    addBackground(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_50' };
        baseTrack(basePayload, ['click', 'use']);

        const params = {
            ['文字特效']: '背景',
        };

        window._stat_track('hb_text_edit_yes_data', params);
    },

    /**
     * 移除背景
     */
    removeBackground(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_50' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 切换可见性
     */
    toggleBackground(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_50' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 调整背景属性
     */
    adjustBackgroundProps(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_50' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 使用划重点
     */
    useTextMark(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_161' };
        baseTrack(basePayload, ['click', 'use']);
    },

    /**
     * 设置划重点属性
     */
    adjustTextMarkProps(payload: Payload & { isColor: boolean }) {
        const basePayload = { ...payload, funcId: '1_161' };

        const timings: Array<'click' | 'use'> = payload.isColor ? ['use'] : ['click', 'use'];
        baseTrack(basePayload, timings);
    },

    /**
     * 取消变形文字
     */
    cancelShapeText(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_54' };
        baseTrack(basePayload, ['click', 'use']);

        window._stat_track('hb_text_edit_yes_data', {
            deformation_effect: 0,
        });
    },

    /**
     * 使用变形文字
     */
    useShapeText(payload: Payload & { shapeType: number; materialID: number }) {
        const { shapeType, materialID, ...rest } = payload;

        const basePayload = { ...rest, funcId: '1_54' };
        baseTrack(basePayload, ['click', 'use']);

        window._stat_track('hb_text_edit_yes_data', {
            deformation_effect: shapeType,
        });

        window._stat_track('tpbj_material_click', {
            type_id: 1088,
            material_id: materialID,
        });
        window._stat_track('tpbj_material_tick', {
            type_id: 1088,
            material_id: materialID,
        });
    },

    /**
     * 调整变形文字属性
     */
    adjustShapeTextProps(payload: Payload) {
        const basePayload = { ...payload, funcId: '1_54' };
        baseTrack(basePayload, ['click', 'use']);
    },
};
